import { FetchSource, PMTiles, Protocol } from "pmtiles";
import maplibregl from "maplibre-gl";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { cdnUrl } from "util/cdn";

export const PMTilesSources = [
  "flooding_1996_pt",
  "flooding_2020_pt",
  "flooding_2020_nomang_pt",
  "flooding_squares",
  "mangroves_1996",
  "mangroves_2015",
  "mangroves_2020",
  "UCSC_CWON_studyunits",
  "UCSC_CWON_studyunits_points",
  "UCSC_CWON_studyunits_hexagons",
  "UCSC_CWON_countrybounds",
] as const;

export type PMTilesSource = (typeof PMTilesSources)[number];

const PMTileURLs: Record<PMTilesSource, string> = {
  flooding_1996_pt: cdnUrl(
    "gcp_export/cwon_data_chunked/with_1996_TC_Tr_050.pmtiles",
  ),
  flooding_2020_pt: cdnUrl(
    "gcp_export/cwon_data_chunked/with_2020_050.pmtiles",
  ),
  flooding_2020_nomang_pt: cdnUrl(
    "gcp_export/cwon_data_chunked/without_2020_050.pmtiles",
  ),
  flooding_squares: cdnUrl("gcp_export/CWON_reindexed/beveled_squares.pmtiles"),
  mangroves_1996: cdnUrl("gcp_export/vectors/GMW_1996_v3_Areas.pmtiles"),
  mangroves_2015: cdnUrl("gcp_export/vectors/GMW_2015_v3_Areas.pmtiles"),
  mangroves_2020: cdnUrl("gcp_export/vectors/GMW_2020_v3.pmtiles"),
  UCSC_CWON_studyunits: cdnUrl(
    "gcp_export/cwon-teselas/UCSC_CWON_studyunits.pmtiles",
  ),
  UCSC_CWON_studyunits_points: cdnUrl(
    "gcp_export/cwon-teselas/UCSC_CWON_studyunits_points.pmtiles",
  ),
  UCSC_CWON_studyunits_hexagons: cdnUrl(
    "gcp_export/cwon-teselas/UCSC_CWON_studyunits_hexagons.pmtiles",
  ),
  UCSC_CWON_countrybounds: cdnUrl(
    "gcp_export/vectors/cwon-countries/UCSC_CWON_countrybounds.pmtiles",
  ),
} as const;

export type PMTilesLayer = {
  layer: string;
  attributes: PMTilesAttribute[];
  attributeCount: number;
  pmTiles: PMTiles;
};

type PMTilesAttribute = {
  attribute: string;
  count: number;
  values: string[];
} & (
  | { type: "number"; max: number; min: number }
  | { type: string; max?: never; min?: never }
);

type PMTilesMetadata = {
  name: string;
  tilestats: {
    layers: PMTilesLayer[];
  };
};

const fetchPMTilesData = async () => {
  const protocol = new Protocol();
  maplibregl.addProtocol("pmtiles", protocol.tile);

  const data = await Promise.all(
    Object.values(PMTileURLs).map(async (source) => {
      try {
        const p = new PMTiles(source);
        protocol.add(p);

        const [header, metadata] = await Promise.all([
          p.getHeader().catch((e: Error) => {
            console.error(`Header fetch failed for ${source}:`, e);
            throw e;
          }),
          p.getMetadata().catch((e: Error) => {
            console.error(`Metadata fetch failed for ${source}:`, e);
            throw e;
          }) as Promise<PMTilesMetadata>,
        ]);

        return [header, metadata];
      } catch (error) {
        console.error(`Failed to load PMTiles for ${source}:`, error);
        throw error;
      }
    }),
  );

  return {
    headers: data.map(([header]) => header),
    metadata: data.map(([, metadata]) => metadata as PMTilesMetadata),
  };
};

function usePMTiles() {
  const { data, isLoading } = useQuery({
    queryKey: ["pmtiles"],
    queryFn: fetchPMTilesData,
    staleTime: Infinity,
  });
  const sources = Object.fromEntries(
    Object.entries(PMTileURLs).map(([key, value]) => [key, value]),
  );

  const layerInfo = useMemo(() => {
    if (!data?.metadata) return undefined;
    return Object.keys(sources).reduce(
      (acc, sourceType) => {
        const index = Object.keys(sources).indexOf(sourceType);
        acc[sourceType] = {
          ...data.metadata[index]!.tilestats?.layers[0]!,
          pmTiles: new PMTiles(new FetchSource(sources[sourceType]!)),
        };
        return acc;
      },
      {} as Record<string, PMTilesLayer>,
    );
  }, [data?.metadata, sources]);

  return {
    headers: data?.headers,
    metadata: data?.metadata,
    layerInfo,
    isLoading,
    sources,
  };
}

export default usePMTiles;
